import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const empty = {
  label: "Home",
  full_name: "",
  phone: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
};

export default function Addresses() {
  const [addresses, setAddresses] = useState([]);
  const [editing, setEditing] = useState(null); // null | { ...address }
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/addresses").then((r) => setAddresses(r.data));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing({ new: true });
    setForm(empty);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      label: a.label || "Home",
      full_name: a.full_name || "",
      phone: a.phone || "",
      line1: a.line1 || "",
      line2: a.line2 || "",
      city: a.city || "",
      state: a.state || "",
      pincode: a.pincode || "",
      country: a.country || "India",
    });
  };

  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing?.new) {
        await api.post("/addresses", form);
        toast.success("Address added");
      } else {
        await api.put(`/addresses/${editing.id}`, form);
        toast.success("Address updated");
      }
      close();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await api.delete(`/addresses/${id}`);
      toast.success("Address deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen" data-testid="addresses-page">
      <div className="max-w-[1200px] mx-auto px-3 md:px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1a1d29]">Address Book</h1>
          <button onClick={openNew} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="address-add-new">
            <Plus className="w-4 h-4" /> Add address
          </button>
        </div>

        {addresses.length === 0 ? (
          <div className="card p-10 text-center">
            <div className="font-display text-xl font-semibold mb-2">No saved addresses</div>
            <div className="text-sm text-[#6b6b6b]">Save an address to speed up future checkouts.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {addresses.map((a) => (
              <div key={a.id} className="card p-4" data-testid={`address-row-${a.id}`}>
                <div className="flex items-start justify-between">
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#c8532e]">{a.label}</div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(a)} className="w-8 h-8 border border-[#e0d8c8] rounded-md flex items-center justify-center hover:bg-[#faf7f2]" data-testid={`address-edit-${a.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(a.id)} className="w-8 h-8 border border-[#e0d8c8] rounded-md flex items-center justify-center hover:bg-[#faf7f2]" data-testid={`address-delete-${a.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 font-semibold">{a.full_name}</div>
                <div className="text-sm text-[#6b6b6b] mt-1">
                  {a.line1}{a.line2 ? `, ${a.line2}` : ""}
                </div>
                <div className="text-sm text-[#6b6b6b]">{a.city}, {a.state} {a.pincode}</div>
                <div className="text-sm text-[#6b6b6b]">{a.country}</div>
                <div className="text-sm text-[#6b6b6b] mt-2">📞 {a.phone}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white rounded-lg max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="address-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-semibold">{editing?.new ? "Add new address" : "Edit address"}</h2>
              <button onClick={close} data-testid="address-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label="Label"><input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className={I} data-testid="af-label" /></F>
              <F label="Full Name"><input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={I} data-testid="af-name" /></F>
              <F label="Phone"><input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={I} data-testid="af-phone" /></F>
              <F label="Address Line 1" full><input required value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} className={I} data-testid="af-line1" /></F>
              <F label="Address Line 2" full><input value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} className={I} data-testid="af-line2" /></F>
              <F label="City"><input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={I} data-testid="af-city" /></F>
              <F label="State"><input required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={I} data-testid="af-state" /></F>
              <F label="Pincode"><input required value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className={I} data-testid="af-pincode" /></F>
              <F label="Country"><input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={I} data-testid="af-country" /></F>
              <div className="col-span-full flex justify-end gap-3 mt-2">
                <button type="button" onClick={close} className="btn-outline text-sm">Cancel</button>
                <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="af-submit">
                  {busy ? "Saving…" : "Save address"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const I = "w-full h-10 bg-white border border-[#e0d8c8] focus:border-[#c8532e] focus:ring-2 focus:ring-[#c8532e]/30 rounded-md outline-none px-3 text-sm";

function F({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs font-semibold text-[#1a1d29]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
